// The weekly hat (PLAN-2026-10-30-the-weekly-hat): the hats a week's high
// scorer may put on the week's low scorer, and the one the league wears until
// somebody picks.
//
// THE FACE MAKER'S OWN HATS (`FACE_HEADWEAR`), the silly ones, so each is drawn
// on any face as any hat is, and perched on a badge that is not a face. No
// crown, tiara or halo: those read as honors, and this is ribbing.
//
// DATA, NOT DRAWINGS, as the faces are: a client draws a hat its own way.

import { z } from 'zod';
import { DEFAULT_FACE_COLORS, FACE_HEADWEAR, type Face, type FaceHeadwear } from './faces';

/** The hats, in the order the picker shows them. */
export const WEEKLY_HATS = [
  'traffic-cone',
  'lampshade',
  'propeller',
  'party',
  'jester',
  'viking',
  'chef',
  'wizard',
  'pirate',
  'bunny-ears',
  'cat-ears',
  'top-hat',
  'cowboy',
  'flowers',
] as const satisfies readonly FaceHeadwear[];

export type WeeklyHat = (typeof WEEKLY_HATS)[number];

export const WeeklyHatSchema = z.enum(WEEKLY_HATS);

/** A hat's name aloud, the face catalog's: *Traffic cone*, *Propeller cap*. */
export function weeklyHatName(hat: WeeklyHat): string {
  return FACE_HEADWEAR.find((p) => p.id === hat)?.name ?? hat;
}

/** The same thing in a sentence: *the traffic cone*. */
export function weeklyHatSaid(hat: WeeklyHat): string {
  return `the ${weeklyHatName(hat).toLowerCase()}`;
}

/** A hat's colors where the catalog brings none (the propeller cap, the party hat): red and gold. */
const PARTY = { hat: '#e53935', hatTrim: '#ffd43b' };

/**
 * A face wearing the week's hat (§7): the hat in place of its own, in the
 * hat's colors, the catalog's where it brings them.
 */
export function wearingHat(face: Face, hat: WeeklyHat): Face {
  const part = FACE_HEADWEAR.find((p) => p.id === hat);
  return { ...face, headwear: hat, colors: { ...face.colors, ...PARTY, ...(part?.paints ?? {}) } };
}

/** The colors a hat is drawn in alone, perched on a badge that is not a face. */
export function hatColors(hat: WeeklyHat): Face['colors'] {
  const part = FACE_HEADWEAR.find((p) => p.id === hat);
  return { ...DEFAULT_FACE_COLORS, ...PARTY, ...(part?.paints ?? {}) };
}

/** FNV-1a: the same week draws the same hat on every read and every machine. */
function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * The hat a week wears until somebody picks (§3): a draw keyed by the league,
 * its season and the week, so it is on the team the moment the week settles.
 */
export function drawnHat(leagueId: string, season: number, period: number): WeeklyHat {
  return WEEKLY_HATS[hash(`hat|${leagueId}|${season}|${period}`) % WEEKLY_HATS.length]!;
}

/**
 * The league's hat as every league view carries it (`LeagueRef.hat`): the
 * week it is for, the hat, the teams wearing it and the teams that may pick
 * it, and the team that picked it, or null while it is the draw's. Added
 * 2026-10-30.
 */
export const LeagueHat = z.object({
  period: z.number().int(),
  hat: WeeklyHatSchema,
  wearers: z.array(z.string()),
  pickers: z.array(z.string()),
  pickedBy: z.string().nullable(),
});
export type LeagueHat = z.infer<typeof LeagueHat>;

/** `PUT /leagues/:id/hat` — the week's high scorer puts a hat on the low scorer. */
export const PickHatRequest = z.object({ hat: WeeklyHatSchema });
export type PickHatRequest = z.infer<typeof PickHatRequest>;
